/**
 * Screen loaders shared by React.lazy and prefetch so Vite emits one chunk per screen.
 */
export const SCREEN_LOADERS = {
  banker: {
    overview: () => import('../ui/screens/banker/BankerOverview'),
    newplay: () => import('../ui/screens/lister/NewPlay'),
    plays: () => import('../ui/screens/banker/BankerPlays'),
    results: () => import('../ui/screens/banker/BankerResults'),
    users: () => import('../ui/screens/banker/BankerUsers'),
    route: () => import('../ui/screens/banker/BankerRoute'),
    config: () => import('../ui/screens/banker/BankerConfig'),
    rates: () => import('../ui/screens/shared/ExchangeRatesScreen'),
  },
  lister: {
    overview: () => import('../ui/screens/lister/ListerOverview'),
    newplay: () => import('../ui/screens/lister/NewPlay'),
    plays: () => import('../ui/screens/banker/BankerPlays'),
    results: () => import('../ui/screens/lister/ListerResults'),
    handoff: () => import('../ui/screens/lister/ListerHandoff'),
    rates: () => import('../ui/screens/shared/ExchangeRatesScreen'),
  },
  runner: {
    overview: () => import('../ui/screens/runner/RunnerOverview'),
    route: () => import('../ui/screens/runner/RunnerRoute'),
    history: () => import('../ui/screens/runner/RunnerHistory'),
  },
  god: {
    overview: () => import('../ui/screens/god/GodOverview'),
    houses: () => import('../ui/screens/god/GodHouses'),
    users: () => import('../ui/screens/banker/BankerUsers'),
    tickets: () => import('../ui/screens/support_agent/SupportTickets'),
    results: () => import('../ui/screens/god/GodResults'),
    knowledge: () => import('../ui/screens/support_agent/SupportKnowledge'),
    audit: () => import('../ui/screens/god/GodAudit'),
    integrity: () => import('../ui/screens/god/GodIntegrity'),
    billing: () => import('../ui/screens/god/GodBilling'),
    config: () => import('../ui/screens/god/GodConfig'),
    rates: () => import('../ui/screens/shared/ExchangeRatesScreen'),
  },
  support_agent: {
    overview: () => import('../ui/screens/support_agent/SupportOverview'),
    tickets: () => import('../ui/screens/support_agent/SupportTickets'),
    knowledge: () => import('../ui/screens/support_agent/SupportKnowledge'),
    audit: () => import('../ui/screens/support_agent/SupportAudit'),
    analytics: () => import('../ui/screens/support_agent/SupportAnalytics'),
  },
} as const;

const EXPORT_NAMES: Record<string, Record<string, string>> = {
  banker: {
    overview: 'BankerOverview',
    newplay: 'NewPlay',
    plays: 'BankerPlays',
    results: 'BankerResults',
    users: 'BankerUsers',
    route: 'BankerRoute',
    config: 'BankerConfig',
    rates: 'ExchangeRatesScreen',
  },
  lister: {
    overview: 'ListerOverview',
    newplay: 'NewPlay',
    plays: 'BankerPlays',
    results: 'ListerResults',
    handoff: 'ListerHandoff',
    rates: 'ExchangeRatesScreen',
  },
  runner: {
    overview: 'RunnerOverview',
    route: 'RunnerRoute',
    history: 'RunnerHistory',
  },
  god: {
    overview: 'GodOverview',
    houses: 'GodHouses',
    users: 'BankerUsers',
    tickets: 'SupportTickets',
    results: 'GodResults',
    knowledge: 'SupportKnowledge',
    audit: 'GodAudit',
    integrity: 'GodIntegrity',
    billing: 'GodBilling',
    config: 'GodConfig',
    rates: 'ExchangeRatesScreen',
  },
  support_agent: {
    overview: 'SupportOverview',
    tickets: 'SupportTickets',
    knowledge: 'SupportKnowledge',
    audit: 'SupportAudit',
    analytics: 'SupportAnalytics',
  },
};

/** Retry a dynamic import a few times — common on flaky mobile links. */
export async function importWithRetry<T>(
  loader: () => Promise<T>,
  attempts = 3,
): Promise<T> {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i += 1) {
    try {
      return await loader();
    } catch (err) {
      lastErr = err;
      await new Promise((r) => setTimeout(r, 300 * (i + 1)));
    }
  }
  throw lastErr;
}

/** Warm the default tab chunk for a role (call once role is known). */
export function prefetchRoleOverview(role: string): void {
  const loaders = SCREEN_LOADERS[role as keyof typeof SCREEN_LOADERS];
  const overview = loaders?.overview;
  if (!overview) return;
  void importWithRetry(() => overview() as Promise<unknown>).catch(() => {
    // Prefetch is best-effort.
  });
  // Warm a couple of high-traffic secondary tabs after the main chunk, when idle.
  const secondaryByRole: Record<string, string[]> = {
    banker: ['plays', 'results'],
    lister: ['newplay', 'results'],
    runner: ['route'],
    god: ['houses', 'results'],
    support_agent: ['tickets'],
  };
  const secondary = secondaryByRole[role] ?? [];
  const warmSecondary = () => {
    for (const tab of secondary) {
      const loader = (loaders as Record<string, (() => Promise<unknown>) | undefined>)[tab];
      if (!loader) continue;
      void importWithRetry(() => loader()).catch(() => {});
    }
  };
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => warmSecondary(), { timeout: 2500 });
  } else {
    window.setTimeout(warmSecondary, 500);
  }
}

export function screenExportName(role: string, tab: string): string | undefined {
  return EXPORT_NAMES[role]?.[tab];
}
